import React, { useCallback, useMemo, useState } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, TextInput, ScrollView } from 'react-native';
import { useNavigation } from '@react-navigation/native';

import { DesktopAdminLayout } from '@/components/common/admin/desktop/DesktopAdminLayout';
import { DesktopPageHeader } from '@/components/common/admin/desktop/DesktopPageHeader';
import { FormSection, FormInput } from '@/components';
import { RecipientSelector } from '@/components/admin/messages/CreateMessage/components/RecipientSelector';
import { MessageTypeSelector } from '@/components/admin/messages/CreateMessage/components/MessageTypeSelector';
import { FileUploadSection } from '@/components/common/admin/mobile/FileUploadSection';
import { useCreateConversationMutation } from '@/store/api/admin/messagesApi';
import { useAdminUser } from '@/hooks/admin/useAdminUser';
import { useToast, useErrorToast } from '@/contexts/admin/ToastContext';
import { ADMIN_COLORS } from '@/constants/admin';
import { SVG_COMPONENTS } from '@/assets/admin';
import { useCreateMessageForm } from '@/components/admin/messages/CreateMessage/hooks/useCreateMessageForm';
import { IMessageResident, ILeaseCustomer } from '@/types/admin/messages';
import { GroupChatConflictNotice } from '@/components/admin/messages/CreateMessage/components/GroupChatConflictNotice';
import { UnassignedRecipientsNotice } from '@/components/admin/messages/CreateMessage/components/UnassignedRecipientsNotice';
import { buildCreateConversationFormData } from '@/components/admin/messages/CreateMessage/utils/formData';
import { WebConfirmModal } from '@/components/common/modals/desktop/DesktopConfirmModal';

export const DesktopCreateMessageView: React.FC = () => {
  const navigation = useNavigation<any>();
  const { showSuccess } = useToast();
  const { showApiError } = useErrorToast();
  const adminUser = useAdminUser();
  const selectedPropertyId = adminUser.selectedPropertyId;

  const [createConversation, { isLoading }] = useCreateConversationMutation();
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);

  const {
    formData,
    errors,
    validateForm,
    handleFieldChange,
    showDiscardAlert,
    hasUnsavedChanges,
    isFormValid,
    getRecipientStats,
  } = useCreateMessageForm();

  const disableSend = useMemo(() => {
    if (isLoading) return true;
    if (!formData.subject || !formData.messageContent) return true;
    if (formData.recipients.length === 0) return true;
    return false;
  }, [isLoading, formData.subject, formData.messageContent, formData.recipients.length]);

  const handleBack = useCallback(() => {
    // Always confirm on desktop to match requested behavior
    setShowCancelConfirm(true);
  }, []);

  const handleSubmit = useCallback(async () => {
    if (!selectedPropertyId) {
      showApiError('No property selected');
      return;
    }

    if (isLoading || disableSend) {
      return;
    }

    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      showApiError('Please fix the form errors before submitting');
      return;
    }

    try {
      const formDataToSend = buildCreateConversationFormData(formData as any, selectedPropertyId);
      const result = await createConversation(formDataToSend).unwrap();
      if (result?.success) {
        showSuccess('Message created successfully');
        navigation.goBack();
      } else {
        showApiError('Failed to create message');
      }
    } catch (error: any) {
      const errorMessage = error?.data?.message || 'Failed to create message';
      showApiError(errorMessage);
    }
  }, [selectedPropertyId, isLoading, disableSend, validateForm, formData, createConversation, showSuccess, showApiError, navigation]);

  const recipientStats = getRecipientStats();

  return (
    <DesktopAdminLayout>
      <DesktopPageHeader title="New Message">
        <View style={styles.headerActions}>
          <TouchableOpacity style={styles.cancelButton} onPress={handleBack}>
            <Text style={styles.cancelText}>Cancel</Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={[styles.sendButton, disableSend && styles.sendButtonDisabled]}
            onPress={handleSubmit}
            disabled={disableSend}
          >
            <Text style={styles.sendText}>Send</Text>
          </TouchableOpacity>
        </View>
      </DesktopPageHeader>

      <View style={styles.row}>
        {/* Left pane: form */}
        <View style={styles.leftPane}> 
          <ScrollView style={styles.leftScroll} contentContainerStyle={styles.leftContent}>
            <FormSection title="Message Details">
              <FormInput
                label="Subject"
                value={formData.subject}
                onChangeText={(value) => handleFieldChange('subject', value)}
                placeholder="Enter message subject"
                error={errors.subject}
                required
                maxLength={100}
              />
            </FormSection>

            <RecipientSelector
              recipients={formData.recipients}
              onRecipientsChange={(recipients) => handleFieldChange('recipients', recipients)}
              error={errors.recipients}
              messageType={formData.messageType}
            />

            <MessageTypeSelector
              messageType={formData.messageType}
              onMessageTypeChange={(type) => handleFieldChange('messageType', type)}
              recipientCount={formData.recipients.length}
            />

            {/* Group chat conflict (guarantors with mixed leases) */}
            <GroupChatConflictNotice mismatchedGuarantors={formData.mismatchedGuarantors as any} />

            {/* Unassigned recipients (invitation flow) */}
            <UnassignedRecipientsNotice recipients={formData.recipients as any} />

            <FileUploadSection
              title="Attachments"
              subtitle="Add files to your message (optional)"
              files={formData.files}
              onFilesChange={(files) => handleFieldChange('files', files)}
              maxFiles={5}
            />

            <FormSection title="Notification Settings">
              <TouchableOpacity
                style={styles.toggleRow}
                onPress={() => handleFieldChange('sendEmail', !formData.sendEmail)}
              >
                <View style={styles.toggleContent}>
                  <Text style={styles.toggleLabel}>Send email notification</Text>
                  <Text style={styles.toggleSubtext}>
                    Recipients will receive an email notification about this message
                  </Text>
                </View>
                <View style={[styles.toggle, formData.sendEmail && styles.toggleActive]}>
                  {formData.sendEmail && (
                    <SVG_COMPONENTS.CHECK_CIRCLE width={16} height={16} color="#FFFFFF" />
                  )}
                </View>
              </TouchableOpacity>
            </FormSection>
          </ScrollView>
        </View>

        {/* Right pane: compose */}
        <View style={styles.rightPane}>
          <View style={styles.composeHeader}>
            <View style={styles.composeHeaderText}>
              <Text style={styles.composeTitle} numberOfLines={1}>
                {formData.subject || 'Untitled message'}
              </Text>
              <Text style={styles.composeSubtitle} numberOfLines={1}>
                {recipientStats.total > 0
                  ? `${recipientStats.total} Recipient${recipientStats.total === 1 ? '' : 's'}`
                  : 'No recipients selected'}
              </Text>
            </View>
          </View>

          <View style={styles.composeBody}>
            <View style={styles.inputContainer}>
              <TextInput
                style={styles.textInput}
                value={formData.messageContent}
                onChangeText={(v) => handleFieldChange('messageContent', v)}
                placeholder="Your message..."
                placeholderTextColor={ADMIN_COLORS.TEXT_TERTIARY}
                multiline
                maxLength={1000}
                textAlignVertical="top"
              />
            </View>

            {formData.files?.length > 0 && (
              <View style={styles.attachSummary}>
                <SVG_COMPONENTS.PAPERCLIP width={16} height={16} color={ADMIN_COLORS.TEXT_SECONDARY} />
                <Text style={styles.attachSummaryText}>{formData.files.length} attachment{formData.files.length === 1 ? '' : 's'}</Text>
              </View>
            )}
          </View>

          <View style={styles.footerBar}>
            <TouchableOpacity
              style={[styles.footerSendButton, disableSend && styles.footerSendButtonDisabled]}
              onPress={handleSubmit}
              disabled={disableSend}
            >
              <Text style={styles.footerSendText}>Send Message</Text>
            </TouchableOpacity>
          </View>
        </View>
      </View>
      <WebConfirmModal
        visible={showCancelConfirm}
        title={'Discard Changes'}
        message={'You have unsaved changes. Are you sure you want to discard them?'}
        confirmText={'Discard'}
        cancelText={'Keep Editing'}
        onConfirm={() => {
          setShowCancelConfirm(false);
          navigation.goBack();
        }}
        onCancel={() => setShowCancelConfirm(false)}
      />
    </DesktopAdminLayout>
  );
};

const styles = StyleSheet.create({
  headerActions: {
    flexDirection: 'row',
    gap: 8,
  },
  cancelButton: {
    paddingHorizontal: 12,
    paddingVertical: 10,
    borderRadius: 8,
    backgroundColor: ADMIN_COLORS.BACKGROUND.SECONDARY,
  },
  cancelText: {
    color: ADMIN_COLORS.TEXT.PRIMARY,
    fontWeight: '600',
  },
  sendButton: {
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderRadius: 8,
    backgroundColor: ADMIN_COLORS.PRIMARY,
  },
  sendButtonDisabled: {
    backgroundColor: ADMIN_COLORS.BACKGROUND.SECONDARY,
    opacity: 0.6,
  },
  sendText: {
    color: '#FFFFFF',
    fontWeight: '700',
  },
  row: {
    flexDirection: 'row',
    gap: 12,
    flex: 1,
    minHeight: 360,
  },
  leftPane: {
    width: 420,
    maxWidth: 480,
    alignSelf: 'stretch',
    backgroundColor: ADMIN_COLORS.BACKGROUND.PRIMARY,
    borderWidth: 1,
    borderColor: ADMIN_COLORS.BORDER,
    borderRadius: 12,
    overflow: 'hidden',
  },
  leftScroll: {
    flex: 1,
  },
  leftContent: {
    padding: 12,
    paddingBottom: 24,
  },
  rightPane: {
    flex: 1,
    alignSelf: 'stretch',
    backgroundColor: ADMIN_COLORS.BACKGROUND.PRIMARY,
    borderWidth: 1,
    borderColor: ADMIN_COLORS.BORDER,
    borderRadius: 12,
    overflow: 'hidden',
  },
  composeHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: ADMIN_COLORS.BORDER,
  },
  composeHeaderText: {
    flex: 1,
    marginRight: 12,
  },
  composeTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: ADMIN_COLORS.TEXT.PRIMARY,
    marginBottom: 2,
  },
  composeSubtitle: {
    fontSize: 14,
    color: ADMIN_COLORS.TEXT_SECONDARY,
  },
  composeBody: {
    flex: 1,
    padding: 16,
    gap: 12,
  },
  inputContainer: {
    backgroundColor: ADMIN_COLORS.BACKGROUND.SECONDARY,
    borderRadius: 12,
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderWidth: 1,
    borderColor: ADMIN_COLORS.BORDER,
    minHeight: 160,
  },
  textInput: {
    minHeight: 140,
    fontSize: 16,
    color: ADMIN_COLORS.TEXT.PRIMARY,
  },
  attachSummary: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  attachSummaryText: {
    fontSize: 14,
    color: ADMIN_COLORS.TEXT_SECONDARY,
  },
  // Toggle styles (desktop)
  toggleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 16,
    paddingHorizontal: 16,
    backgroundColor: ADMIN_COLORS.BACKGROUND.SECONDARY,
    borderRadius: 12,
    marginTop: 8,
  },
  toggleContent: {
    flex: 1,
    marginRight: 16,
  },
  toggleLabel: {
    fontSize: 16,
    fontWeight: '500',
    color: ADMIN_COLORS.TEXT.PRIMARY,
    marginBottom: 4,
  },
  toggleSubtext: {
    fontSize: 14,
    color: ADMIN_COLORS.TEXT_SECONDARY,
    lineHeight: 18,
  },
  toggle: {
    width: 24,
    height: 24,
    borderRadius: 4,
    borderWidth: 2,
    borderColor: ADMIN_COLORS.BORDER,
    backgroundColor: 'transparent',
    alignItems: 'center',
    justifyContent: 'center',
  },
  toggleActive: {
    backgroundColor: ADMIN_COLORS.PRIMARY,
    borderColor: ADMIN_COLORS.PRIMARY,
  },
  footerBar: {
    padding: 12,
    borderTopWidth: 1,
    borderTopColor: ADMIN_COLORS.BORDER,
  },
  footerSendButton: {
    backgroundColor: ADMIN_COLORS.PRIMARY,
    paddingVertical: 12,
    borderRadius: 10,
    alignItems: 'center',
  },
  footerSendButtonDisabled: {
    backgroundColor: ADMIN_COLORS.BACKGROUND.SECONDARY,
    opacity: 0.6,
  },
  footerSendText: {
    color: '#FFFFFF',
    fontWeight: '700',
  },
});

export default DesktopCreateMessageView;


